/**
 * Catálogo do domínio `projetos_fabrica` (T53, CAT-02).
 *
 * Origem: `src/shared/autorizacao/projetos_fabrica.ts`.
 *
 * O `read` em `FABRICACAO` acompanha os três níveis porque o arquivo de origem o
 * concede em cada um deles (linhas 9, 13 e 17). Não é redundante com a base de
 * `fabricacao`: são domínios distintos, e a base de fabricação pode cair sem
 * levar esta junto.
 *
 * **`aprovador_fabrica` (`projetos_fabrica.ts:23`) resolve pelo `tipo`, na
 * T129.** É uma das quatro strings ambíguas Gali×cliente do §4.2: do lado Gali
 * concede só `update`@`PROJETO` com `Situacao: ["PAP"]` (um recurso de outro
 * domínio, e por isso fora desta slice); do lado cliente, `cliente.ts:19` dava
 * três ações e quatro estados para a mesma string — reduzido a `["PAP"]` pela
 * decisão D4b do AD-031 (`projetos.aprovacao`, ver `papeis/projetos.ts`). A
 * T129 resolve por `tipo`, não por nome — `shared/autorizacao/index.ts`.
 */
import type { NomePapel, Papel } from "../catalogo/vocabulario";

export const PAPEIS_PROJETOS_FABRICA = {
  "projetos_fabrica.admin": {
    dominio: "projetos_fabrica",
    nivel: "admin",
    concessoes: [
      { acoes: ["manage"], recurso: "PROJETO_FABRICA" },
      { acoes: ["adicionar_cliente"], recurso: "PROJETO_FABRICA" },
      { acoes: ["read"], recurso: "FABRICACAO" },
    ],
  },
  "projetos_fabrica.escrita": {
    dominio: "projetos_fabrica",
    nivel: "escrita",
    concessoes: [
      { acoes: ["read", "create", "update"], recurso: "PROJETO_FABRICA" },
      { acoes: ["read"], recurso: "FABRICACAO" },
    ],
  },
  "projetos_fabrica.upload": {
    dominio: "projetos_fabrica",
    nivel: "upload",
    concessoes: [{ acoes: ["upload"], recurso: "PROJETO_FABRICA" }],
  },
  /** `projetos_fabrica.ts:12` concede a `projetista_fabrica`, `projetista` e `site_manager`. */
  "projetos_fabrica.leitura": {
    dominio: "projetos_fabrica",
    nivel: "leitura",
    concessoes: [
      { acoes: ["read"], recurso: "PROJETO_FABRICA" },
      { acoes: ["read"], recurso: "FABRICACAO" },
    ],
  },
  /**
   * A leitura do cliente sobre projeto de fábrica (T129, prerequisito 3):
   * `cliente.ts:36` concedia `read` em `PROJETO_FABRICA`, `FABRICACAO` e
   * `Produto` para `projetista_fabrica` — sem a distinção interno/externo que
   * `projetos.leitura_externa` já faz do lado de `projetos`, porque
   * `projetos_fabrica.ts` (Gali) não tem um nível "interno" a excluir: o
   * `read` daqui é o mesmo que o Gali recebe.
   */
  "projetos_fabrica.leitura_externa": {
    dominio: "projetos_fabrica",
    nivel: "leitura_externa",
    concessoes: [
      { acoes: ["read"], recurso: "PROJETO_FABRICA" },
      { acoes: ["read"], recurso: "FABRICACAO" },
      { acoes: ["read"], recurso: "Produto" },
    ],
  },
} as const satisfies Partial<Record<NomePapel, Papel>>;
